import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { VideoItem } from '@/mocks/videos';
import { formatPlays } from '@/utils/videoUtils';

interface HeroCarouselProps {
  items: VideoItem[];
}

export default function HeroCarousel({ items }: HeroCarouselProps) {
  const [active, setActive] = useState(0);
  const count = items.length;

  const goTo = useCallback((index: number) => {
    setActive(((index % count) + count) % count);
  }, [count]);

  useEffect(() => {
    if (count <= 1) return;
    const timer = setInterval(() => {
      setActive((prev) => (prev + 1) % count);
    }, 6000);
    return () => clearInterval(timer);
  }, [count]);

  if (count === 0) return null;
  const current = items[active];

  return (
    <section className="relative w-full h-[440px] md:h-[600px] overflow-hidden bg-secondary-950">
      {items.map((item, index) => (
        <div
          key={item.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            index === active ? 'opacity-100' : 'opacity-0 pointer-events-none'
          }`}
        >
          <img
            src={item.cover}
            alt={`${item.title}焦点图`}
            title={`${item.title} - 在线观看`}
            className="w-full h-full object-cover object-top"
          />
        </div>
      ))}

      <div className="absolute inset-0 bg-gradient-to-r from-secondary-950/90 via-secondary-950/60 to-secondary-950/20"></div>
      <div className="absolute inset-0 bg-gradient-to-t from-secondary-950/80 via-transparent to-transparent"></div>

      <div className="relative z-10 w-full h-full flex items-end md:items-center">
        <div className="w-full px-4 md:px-6 pb-12 md:pb-0">
          <div key={current.id} className="max-w-2xl animate-fade-slide">
            <div className="flex items-center gap-2 mb-4">
              <span className="px-3 py-1 rounded-full bg-primary-500 text-background-50 text-xs font-medium">
                {current.type}
              </span>
              <span className="px-3 py-1 rounded-full bg-accent-500/90 text-background-50 text-xs font-semibold flex items-center gap-1">
                <i className="ri-fire-line"></i>
                热度 {formatPlays(current.plays)}
              </span>
            </div>
            <h1 className="font-display text-3xl md:text-5xl font-bold text-background-50 leading-tight">
              {current.title}
            </h1>
            <p className="mt-3 text-sm md:text-base text-background-200 max-w-xl leading-relaxed line-clamp-2">
              {current.subtitle}
            </p>
            <div className="mt-4 flex items-center flex-wrap gap-x-4 gap-y-2 text-xs md:text-sm text-background-300">
              <span className="flex items-center gap-1">
                <i className="ri-star-fill text-accent-400"></i>
                {current.rating.toFixed(1)} 分
              </span>
              <span>{current.year}</span>
              <span>{current.region}</span>
              <span>{current.duration}</span>
            </div>
            <div className="mt-6 flex items-center flex-wrap gap-3">
              <Link
                to={`/detail/${current.id}`}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium whitespace-nowrap transition-colors cursor-pointer"
              >
                <i className="ri-play-fill text-lg"></i>
                立即观看
              </Link>
              <Link
                to={`/category/${current.category}`}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-background-50/15 hover:bg-background-50/25 text-background-50 text-sm font-medium whitespace-nowrap backdrop-blur transition-colors cursor-pointer"
              >
                查看同类
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="absolute bottom-5 right-4 md:right-6 z-10 flex items-center gap-2">
        {items.map((item, index) => (
          <button
            key={item.id}
            type="button"
            aria-label={`切换到第${index + 1}张`}
            onClick={() => goTo(index)}
            className={`h-1.5 rounded-full transition-all duration-300 cursor-pointer ${
              index === active ? 'w-8 bg-accent-500' : 'w-3 bg-background-50/50 hover:bg-background-50/80'
            }`}
          />
        ))}
      </div>

      <button
        type="button"
        aria-label="上一张"
        onClick={() => goTo(active - 1)}
        className="hidden md:flex absolute left-4 top-1/2 -translate-y-1/2 z-10 w-10 h-10 items-center justify-center rounded-full bg-background-50/15 hover:bg-background-50/30 text-background-50 backdrop-blur transition-colors cursor-pointer"
      >
        <i className="ri-arrow-left-s-line text-2xl"></i>
      </button>
      <button
        type="button"
        aria-label="下一张"
        onClick={() => goTo(active + 1)}
        className="hidden md:flex absolute right-4 top-1/2 -translate-y-1/2 z-10 w-10 h-10 items-center justify-center rounded-full bg-background-50/15 hover:bg-background-50/30 text-background-50 backdrop-blur transition-colors cursor-pointer"
      >
        <i className="ri-arrow-right-s-line text-2xl"></i>
      </button>
    </section>
  );
}